import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
import SalesMapListPage from 'src/pages/dashboard/sales-map/list';
import CampaignRouteView from 'src/sections/campaign-route/view/campaign-route-view';
import GoBackListPage from 'src/pages/dashboard/go-back/list';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
// PRODUCT
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
// BLOG
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// JOB
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// TOUR
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
//  VEHICLES
const VehiclesListPage = lazy(() => import('src/pages/dashboard/vehicle/list'));
//   DRIVERS
const DriversListPage = lazy(() => import('src/pages/dashboard/driver/list'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
//LOGS PAGE
const LogsListPage = lazy(() => import('src/pages/dashboard/logs/list'));
//   REVZONES MANAGEMENT
const RevzonesMapPage = lazy(() => import('src/pages/dashboard/revzones-map/list'));
const RecentActivityPage = lazy(() => import('src/pages/dashboard/recent-activity/list'));
// Leads & Performance
const LeadsAndPerformanceListPage = lazy(() =>
  import('src/pages/dashboard/leads-and-performance/list')
);
// Appointments
const AppointmentsListPage = lazy(() => import('src/pages/dashboard/appointments/list'));
// My Courses
const MyCoursesListPage = lazy(() => import('src/pages/dashboard/my-courses/list'));
// All Courses
const AllCoursesListPage = lazy(() => import('src/pages/dashboard/all-courses/list'));
// Company Courses
const CompanyCoursesListPage = lazy(() => import('src/pages/dashboard/company-courses/list'));
// All Courses Sections
const AllCoursesSectionsListPage = lazy(() => import('src/pages/dashboard/all-courses-sections/list'));
// Company Courses Sections
const CompanyCoursesSectionsListPage = lazy(() => import('src/pages/dashboard/company-courses-sections/list'));
//Settings
const SettingsPage = lazy(() => import('src/pages/dashboard/settings/list'));
// Video Player
const VideosListPage = lazy(() => import('src/pages/dashboard/video-player/list'));
// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      // {
      //   path: 'logs',
      //   element: (
      //     <RoleBasedGuard hasContent name="campaign" sx={{ py: 10 }}>
      //       <LogsListPage />
      //     </RoleBasedGuard>
      //   ),
      // },
      // {
      //   path: 'campaigns/view',
      //   element: (
      //     // <RoleBasedGuard hasContent name="campaign" sx={{ py: 10 }}>
      //     // </RoleBasedGuard>
      //     <CampaignRouteView />
      //   ),
      // },
      {
        path: 'my-courses',
        element: <Outlet />, // This route just renders the children
        children: [
          {
            path: ':id',
            element: <MyCoursesListPage />, // Only shows for /my-courses
          },
          {
            path: ':id/section/:sectionId/lesson/:lessonId',
            element: <VideosListPage />, // Shows for detail route
          },
        ]
      },
      // {
      //   path: 'all-courses',
      //   element: (
      //     // <RoleBasedGuard hasContent name="campaign" sx={{ py: 10 }}>
      //     // </RoleBasedGuard>
      //     <AllCoursesListPage />
      //   ),
      // },
      {
        path: 'company-courses',
        element: (
          // <RoleBasedGuard hasContent name="campaign" sx={{ py: 10 }}>
          // </RoleBasedGuard>
          <CompanyCoursesListPage />
        ),
      },
      {
        path: 'course-sections',
        element: (
          // <RoleBasedGuard hasContent name="campaign" sx={{ py: 10 }}>
          // </RoleBasedGuard>
          <AllCoursesSectionsListPage />
        ),
      },
      {
        path: 'company-course-sections',
        element: (
          // <RoleBasedGuard hasContent name="campaign" sx={{ py: 10 }}>
          // </RoleBasedGuard>
          <CompanyCoursesSectionsListPage />
        ),
      },
      // {
      //   path: 'vehicle',
      //   element: (
      //     <RoleBasedGuard hasContent name="vehicle" sx={{ py: 10 }}>
      //       <VehiclesListPage />
      //     </RoleBasedGuard>
      //   ),
      // },
      // {
      //   path: 'driver',
      //   element: (
      //     <RoleBasedGuard hasContent name="driver" sx={{ py: 10 }}>
      //       <DriversListPage />
      //     </RoleBasedGuard>
      //   ),
      // },
      {
        path: 'revzones-map',
        element: (
          // <RoleBasedGuard hasContent name="division" sx={{ py: 10 }}>
          <RevzonesMapPage />
          // </RoleBasedGuard>
        ),
      },
      {
        path: 'leads-and-performance',
        element: (
          // <RoleBasedGuard hasContent name="division" sx={{ py: 10 }}>
          <LeadsAndPerformanceListPage />
          // </RoleBasedGuard>
        ),
      },
      {
        path: 'crm',
        element: (
          // <RoleBasedGuard hasContent name="division" sx={{ py: 10 }}>
          <AppointmentsListPage />
          // </RoleBasedGuard>
        ),
      },
      {
        path: 'activity-and-reminders/recent-activity',
        element: (
          // <RoleBasedGuard hasContent name="division" sx={{ py: 10 }}>
          <RecentActivityPage />
          // </RoleBasedGuard>
        ),
      },
      {
        path: 'activity-and-reminders/go-backs',
        element: (
          // <RoleBasedGuard hasContent name="division" sx={{ py: 10 }}>
          <GoBackListPage />
          // </RoleBasedGuard>
        ),
      },
      {
        path: 'settings',
        element: (
          // <RoleBasedGuard hasContent name="division" sx={{ py: 10 }}>
          <SettingsPage />
          // </RoleBasedGuard>
        ),
      },
      {
        path: 'sales-map',
        element: <SalesMapListPage />,
      },
      {
        path: 'user',
        children: [
          { element: <UserProfilePage />, index: true },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'account', element: <UserAccountPage /> },
        ],
      },
      {
        path: 'product',
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },
      {
        path: 'invoice',
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
    ],
  },
];
